import { css } from "emotion";
import type { FC } from "react";
import { useRef, useEffect, useMemo } from "react";
import { getAvatarLetters } from "@certa/blocks/thanos";
import type { CommentMention, ConversationThread } from "@certa/types";
import { format, parseISO } from "date-fns";
import { useCommentMessageFormat } from "../../hooks/useCommentMessageFormat";
import {
  Typography,
  TypographyVariants,
  TypographyColors
} from "@certa/catalyst/components/Typography";
import {
  AvatarSizes,
  Avatar as CatalystAvatar,
  AvatarColors
} from "@certa/catalyst/components/Avatar";
import { AttachmentView } from "./AttachmentView";

import { useScreenResolution } from "@certa/common/hooks/useScreenResolution";
import { useCommsNewEndpoints } from "@certa/common/toggles";
import {
  useGetConversationMessagesComms,
  type ConversationMessagesModel
} from "@certa/queries/queries/comms";
import { FullHeightSpinner } from "@certa/common/components/FullHeightSpinner";
import { DateHeader } from "./DateHeader";
import { GroupedMessagesWrapper } from "./GroupedMessagesWrapper";
import type {
  OptimisticConversationMessagesModel,
  OptimisticId
} from "../../util/optimisticMessage.utils";
import { useAuth } from "@certa/auth/AuthenticationProvider";
import { Stack } from "@certa/catalyst/layouts/Stack";

export type MessageProps = {
  postedBy: string;
  createdAt: string;
  attachment: string;
  message: string;
  postedById: number;
  messageId: OptimisticId | number | null;
};

const MessageViewIdentifier = ({
  activeConversationThreadComms,
  groupedMessages,
  mentions,
  optimisticMessages
}: {
  activeConversationThreadComms: ConversationThread | null;
  groupedMessages: { [date: string]: MessageProps[] };
  mentions: CommentMention[];
  optimisticMessages?: (
    | OptimisticConversationMessagesModel
    | ConversationMessagesModel
  )[];
}) => {
  const isCommsNewEndpointEnabled = useCommsNewEndpoints();

  return isCommsNewEndpointEnabled ? (
    <MessageViewNewComms
      activeConversationThreadComms={activeConversationThreadComms}
      mentions={mentions}
      optimisticMessages={optimisticMessages || []}
    />
  ) : (
    <MessageViewNew groupedMessages={groupedMessages} mentions={mentions} />
  );
};

const NewMessage = ({
  attachment,
  postedBy,
  createdAt,
  mentions,
  message,
  userId,
  postedById
}: MessageProps & { mentions: CommentMention[]; userId: number }) => {
  const formattedMessage = useCommentMessageFormat(message, mentions, true);

  const postedAt = format(new Date(createdAt), "hh:mm a");

  return (
    <Stack
      direction="horizontal"
      justify={postedById === userId ? "end" : "start"}
      width="100%"
      css={css({
        textAlign: postedById === userId ? "right" : "left"
      })}
    >
      {/* Title */}
      <Stack
        direction="horizontal"
        maxWidth="65%"
        overflow="auto"
        css={css({
          marginBottom: "var(--space-32)"
        })}
      >
        <Stack
          direction="horizontal"
          gap="8"
          align="start"
          width="100%"
          css={css({
            marginLeft: postedById === userId ? "auto" : "unset"
          })}
        >
          <CatalystAvatar
            size={AvatarSizes.BIG}
            color={AvatarColors.BRAND}
            aria-label={postedBy}
          >
            {getAvatarLetters(postedBy)}
          </CatalystAvatar>
          <Stack
            direction="vertical"
            gap="4"
            dangerouslySetClassName={css({
              minWidth: 0
            })}
          >
            <Stack direction="horizontal" gap="8">
              <Typography
                className={css({ marginTop: "var(--space-2)" })}
                color={TypographyColors.NEUTRAL_800}
                variant={TypographyVariants.LABEL_SM_BOLD}
              >
                {postedBy}
              </Typography>
              <Typography
                variant={TypographyVariants.LABEL_SM}
                color={TypographyColors.NEUTRAL_600}
              >
                {postedAt}
              </Typography>
            </Stack>

            <Stack
              direction="vertical"
              dangerouslySetClassName={css({
                overflowWrap: "break-word",
                minWidth: 0
              })}
            >
              <Typography
                variant={TypographyVariants.BODY}
                color={TypographyColors.NEUTRAL_700}
              >
                <div
                  dangerouslySetInnerHTML={{ __html: formattedMessage }}
                ></div>
              </Typography>
              <Stack
                dangerouslySetClassName={css({
                  minWidth: "0"
                })}
              >
                <AttachmentView
                  attachment={attachment}
                  isPostedByCurrentUser={postedById === userId}
                />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

const MessageViewNewComms = ({
  activeConversationThreadComms,
  mentions,
  optimisticMessages
}: {
  activeConversationThreadComms: ConversationThread | null;
  mentions: CommentMention[];
  optimisticMessages: (
    | OptimisticConversationMessagesModel
    | ConversationMessagesModel
  )[];
}) => {
  const {
    data: messages,
    isLoading: isLoadingMessages,
    hasNextPage,
    fetchNextPage
  } = useGetConversationMessagesComms(
    activeConversationThreadComms?.conversationId!
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const isLoadingOlderMessages = useRef(false);
  const { userId } = useAuth();

  const groupedMessages = useMemo(() => {
    const serverMessages = messages?.pages
      ? messages.pages.flatMap(page => page.results)
      : [];

    // Get all server message IDs for filtering duplicates
    const serverMessageIds = new Set(
      serverMessages.map(msg => msg.kryptonId).filter(id => id != null)
    );

    // Filter out optimistic messages that have matching server messages
    const filteredOptimisticMessages = optimisticMessages.filter(
      optimisticMsg => {
        if (
          optimisticMsg.conversationId !==
          activeConversationThreadComms?.conversationId
        ) {
          return false;
        }
        // If optimistic message has a real ID (meaning it was replaced), check for duplicates
        if (
          optimisticMsg.kryptonId &&
          typeof optimisticMsg.kryptonId === "number"
        ) {
          return !serverMessageIds.has(optimisticMsg.kryptonId);
        }
        // Keep optimistic messages that still have temporary IDs
        return true;
      }
    );

    // Combine server messages with filtered optimistic messages
    const allMessages = [...serverMessages, ...filteredOptimisticMessages];

    if (allMessages.length === 0) {
      return {};
    }

    const sortedMessages = [...allMessages].sort(
      (a, b) => new Date(a.postedAt).getTime() - new Date(b.postedAt).getTime()
    );

    return sortedMessages.reduce<{ [date: string]: MessageProps[] }>(
      (acc, message) => {
        const date = format(parseISO(message.postedAt), "MMMM d, yyyy");
        if (!acc[date]) acc[date] = [];
        acc[date].push({
          postedBy: message.postedBy.fullName || message.postedBy.email,
          createdAt: message.postedAt,
          attachment: message.attachments[0]?.path || "",
          message: message.body,
          postedById: message.postedBy.id,
          messageId: message.kryptonId
        });
        return acc;
      },
      {}
    );
  }, [
    messages,
    optimisticMessages,
    activeConversationThreadComms?.conversationId
  ]);

  const scrollToBottom = (container: HTMLElement) => {
    container.scrollTo(0, container.scrollHeight);
  };

  useEffect(() => {
    if (containerRef?.current && !isLoadingOlderMessages.current) {
      scrollToBottom(containerRef.current);
    }
    isLoadingOlderMessages.current = false;
  }, [groupedMessages]);

  const handleLoadMore = () => {
    isLoadingOlderMessages.current = true;
    fetchNextPage();
  };

  const { isMobileResolution } = useScreenResolution();

  return isLoadingMessages ? (
    <FullHeightSpinner />
  ) : (
    <div
      className={css({
        maxHeight: "100%",
        overflowY: "auto",
        padding: isMobileResolution
          ? "0 var(--space-8)"
          : "0 var(--space-12) 0 0",
        marginBottom: "auto",
        display: "flex",
        flexDirection: "column-reverse"
      })}
      ref={containerRef}
    >
      <GroupedMessagesWrapper
        groupedOptions={Object.entries(groupedMessages).map(
          ([date, messages]) =>
            messages.map(message => ({
              date,
              message
            }))
        )}
        aria-label="messages list"
        getKey={item => `${item.message.messageId}-${item.date}`}
        getLabel={item => item.message.message}
        onClick={() => {}}
        render={item => (
          <div
            className={css({
              width: "100%"
            })}
          >
            <NewMessage {...item.message} mentions={mentions} userId={userId} />
            <div style={{ marginBlock: "var(--space-16)" }} />
          </div>
        )}
        loadMore={handleLoadMore}
        hasMore={hasNextPage}
      />
    </div>
  );
};

const MessageViewNew: FC<{
  groupedMessages: { [date: string]: MessageProps[] };
  mentions: CommentMention[];
}> = ({ groupedMessages, mentions }) => {
  const containerRef = useRef(null);
  const { userId } = useAuth();
  const scrollToBottom = (container: HTMLElement) => {
    container.scrollTo(0, container.scrollHeight);
  };

  useEffect(() => {
    if (containerRef?.current) {
      scrollToBottom(containerRef.current);
    }
  }, [groupedMessages]);

  const { isMobileResolution } = useScreenResolution();

  return (
    <div
      className={css({
        maxHeight: "100%",
        overflowY: "auto",
        padding: isMobileResolution
          ? "0 var(--space-8)"
          : "0 var(--space-12) 0 0",
        marginBottom: "auto"
      })}
      ref={containerRef}
    >
      {Object.entries(groupedMessages).map(([date, messages]) => (
        <div key={date}>
          <DateHeader date={date} />
          <div>
            {messages.map((message, index) => (
              <div key={`${message.messageId || index}`}>
                <NewMessage {...message} mentions={mentions} userId={userId} />
                <div
                  style={{
                    marginBlock: "var(--space-16)"
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export { MessageViewIdentifier, MessageViewNewComms };
